import type { FlexProps } from "@qws-org/ui-kit";
import {
  Box,
  Flex,
  Grid,
  Image,
  Title,
  Typography,
  useUiKitComponents,
} from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, GridBlockV2Data, Pages } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type GridBlockV2Props = {
  data: GridBlockV2Data;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (action: Action) => boolean;
} & FlexProps;

export const GridBlockV2: FC<GridBlockV2Props> = ({
  data,
  getUrl,
  onCallAction,
  isControlInactive,
  ...rest
}) => {
  const { Link } = useUiKitComponents();
  const { getDirectusImage } = useDirectus();

  return (
    <Flex
      flexDirection="column"
      align="center"
      p={{ py: 40, px: 15 }}
      {...rest}
      d={{ p: { py: 80 }, ...rest.d }}
    >
      {data.title && (
        <Title
          as="h2"
          size="medium"
          fontWeight="desktop.semibold"
          horizontalAlign="center"
          m={{ mb: 24 }}
          d={{ m: { mb: 80 } }}
        >
          {data.title}
        </Title>
      )}

      {data.description && (
        <Typography
          size="large"
          fontWeight="desktop.semibold"
          horizontalAlign="center"
          lineHeight="body.mobile.large"
          md={{ lineHeight: "body.desktop.large" }}
          m={{ mb: 24 }}
        >
          {data.description}
        </Typography>
      )}

      <Grid
        gap={data.mobile_gap ?? 0}
        cols={data.mobile_columns ?? 1}
        md={{
          cols: data.tablet_columns,
          gap: data.tablet_gap ?? data.mobile_gap ?? 0,
        }}
        d={{
          cols: data.desktop_columns,
          gap: data.desktop_gap ?? data.tablet_gap ?? data.mobile_gap ?? 0,
        }}
        width="100%"
      >
        {data.items?.map((item) => {
          const controlAction = item?.grid_item_v2_id?.action;

          const action = controlAction?.action?.[0];

          const isActionActive = action
            ? !isControlInactive(controlAction)
            : false;
          let href;
          if (isExternalLink(action?.item)) {
            if (isTemplateUrl(action?.item)) {
              href = undefined;
            } else {
              href = action.item.href ?? undefined;
            }
          }
          if (isInternalLink(action?.item)) {
            href = action.item.page ? getUrl(action.item.page) : undefined;
          }
          const handlePressButton = (): void => {
            if (controlAction) {
              onCallAction(controlAction);
            }
          };

          const ActionEl = isActionActive && href ? Link : Box;
          // настройки фона
          const mBg = item?.grid_item_v2_id?.background?.image?.id;
          const mdBg =
            item?.grid_item_v2_id?.background?.tablet_image?.id ?? mBg;
          const dBg =
            item.grid_item_v2_id?.background?.desktop_image?.id ?? mdBg;

          const mBgColor = item?.grid_item_v2_id?.background?.color;
          const mdBgColor =
            item?.grid_item_v2_id?.background?.tablet_color ?? mBgColor;
          const dBgColor =
            item?.grid_item_v2_id?.background?.desktop_color ?? mdBgColor;

          // настройки картинки
          const mIcon = item?.grid_item_v2_id?.image?.image?.id ?? "";
          const mIconWidth =
            item?.grid_item_v2_id?.image?.mobile_image_width ?? undefined;
          const mIconHeight =
            item?.grid_item_v2_id?.image?.mobile_image_height ?? undefined;

          const mdIcon =
            item?.grid_item_v2_id?.image?.tablet_image?.id ?? mIcon;
          const mdIconWidth =
            item?.grid_item_v2_id?.image?.tablet_image_width ?? mIconWidth;
          const mdIconHeight =
            item?.grid_item_v2_id?.image?.tablet_image_height ?? mIconHeight;

          const dIcon =
            item?.grid_item_v2_id?.image?.desktop_image?.id ?? mdIcon;
          const dIconWidth =
            item?.grid_item_v2_id?.image?.desktop_image_width ?? mdIconWidth;
          const dIconHeight =
            item?.grid_item_v2_id?.image?.desktop_image_height ?? mdIconHeight;
          // если текстового контента нет - картинка становится по середине и занимает все пространство
          const isOnlyImage = !(
            item?.grid_item_v2_id?.title ?? item?.grid_item_v2_id?.description
          );

          return (
            <Grid.Item key={item?.id}>
              <ActionEl
                href={href}
                cursor={isActionActive ? "pointer" : "default"}
                onClick={handlePressButton}
                style={{ height: "100%" }}
              >
                <Flex
                  bg={mBg ? getDirectusImage(mBg) : (mBgColor ?? "")}
                  border={{ radius: "big-card" }}
                  flexDirection={"column"}
                  align="center"
                  gap={24}
                  overflow="hidden"
                  p={{ py: isOnlyImage ? 0 : 24, px: isOnlyImage ? 0 : 24 }}
                  height={"100%"}
                  md={{
                    bg: mdBg ? getDirectusImage(mdBg) : (mdBgColor ?? ""),
                    p: { py: isOnlyImage ? 0 : 16 },
                    flexDirection: "row",
                    justify: "center",
                    align: "center",
                  }}
                  d={{ bg: dBg ? getDirectusImage(dBg) : (dBgColor ?? "") }}
                >
                  <Image
                    value={getDirectusImage(mIcon, {
                      width: mIconWidth ? mIconWidth * 2 : undefined,
                      height: mIconHeight ? mIconHeight * 2 : undefined,
                      format: "webp",
                      quality: 85,
                    })}
                    image={{
                      alt: item?.grid_item_v2_id?.image?.alternative_text ?? "",
                    }}
                    mdValue={getDirectusImage(mdIcon, {
                      format: "webp",
                      quality: 85,
                      width: mdIconWidth ? mdIconWidth * 2 : undefined,
                      height: mdIconHeight ? mdIconHeight * 2 : undefined,
                    })}
                    dValue={getDirectusImage(dIcon, {
                      format: "webp",
                      quality: 85,
                      width: dIconWidth ? dIconWidth * 2 : undefined,
                      height: dIconHeight ? dIconHeight * 2 : undefined,
                    })}
                    maxWidth={mIconWidth}
                    maxHeight={mIconHeight}
                    md={{
                      maxWidth: mdIconWidth,
                      maxHeight: mdIconHeight,
                    }}
                    d={{
                      maxWidth: dIconWidth,
                      height: dIconHeight,
                    }}
                  />
                  {!isOnlyImage && (
                    <Box>
                      <Typography
                        m={{ mb: 8 }}
                        maxWidth={280}
                        horizontalAlign="center"
                        fontWeight={"mobile.bold"}
                        fontSize="title.mobile.medium"
                        lineHeight="title.mobile.medium"
                        md={{
                          maxWidth: 200,
                          horizontalAlign: "left",
                          fontWeight: "desktop.bold",
                          lineHeight: "title.desktop.medium",
                          fontSize: "title.desktop.medium",
                        }}
                      >
                        {item?.grid_item_v2_id?.title}
                      </Typography>
                      <Typography
                        maxWidth={280}
                        horizontalAlign="center"
                        fontWeight={"mobile.normal"}
                        fontSize="body.mobile.large"
                        lineHeight="body.mobile.large"
                        md={{
                          maxWidth: 200,
                          horizontalAlign: "left",
                          fontWeight: "desktop.normal",
                          lineHeight: "body.desktop.large",
                          fontSize: "body.desktop.large",
                        }}
                      >
                        {item?.grid_item_v2_id?.description}
                      </Typography>
                    </Box>
                  )}
                </Flex>
              </ActionEl>
            </Grid.Item>
          );
        })}
      </Grid>
    </Flex>
  );
};
